import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { GenericConfirmationDialogComponent } from '../../generic-confirmation-dialog/generic-confirmation-dialog.component';
import { LoggerService } from '../../logging/logger-factory.service';
import { Logger } from '../../logging/logger.interface';
import { MessageBusService } from '../../message-bus.service';
import { LastQuoteCacheService } from '../../last-quote-cache.service';
import { OrderPreviewMarket } from '../../order-preview/order-preview-market';
import { ShellClientService } from '../../shell-communication/shell-client.service';
import { CollarsBucketDto, CollarsPositionDto, ListCollarsBuckets, ListCollarsPositions } from '../../shell-communication/collars-operations';
import {
   AcquireAnalysisBlocks, ANALYSIS_SESSION_TOUCHED_MESSAGE_TYPE, AnalysisFillInput, AnalysisPendingBlockDto, AnalysisSessionTouchedUIMessage, RevokeAnalysisBlocks
} from '../../shell-communication/analysis-session-operations';
import { TimestampsService } from '../../timestamps.service';
import { TradingInstrumentDisplayNameService } from '../../trading-instruments/trading-instrument-display-name.service';
import { PhaExportBlock, PhaExportDialogComponent, PhaExportGroup, PhaExportLeg } from '../../portfolio-hedging-analysis/pha-export-dialog.component';
import { CollarsAnalysisLockService } from '../collars-analysis-lock.service';
import { buildAcquireGroups } from './collars-acquire.groups';

const LEVEL_COMBO = 1;
const LEVEL_GROUP = 2;

/** Open config: the bucket clicked (any level); a position row narrows to its own blocks. */
export interface CollarsAcquireConfig {
   scopeBucketId: string;
   level: number;           // 0 = Portfolio, 1 = Combo, 2 = Group
   analysisBlockIds?: string[];
}

export interface CollarsAcquireResult {
   /** An acquisition was sent (booked, or failed after the send — legs may have landed): reload the book. */
   applied: boolean;
   /** A block was cleared: re-read the pending set (its earmark goes). */
   discarded: boolean;
}

/**
 * The acquisition window of the Portfolio Hedging panel (CR-20260930-001 M3): the Analysis panel's
 * EXPORT WINDOW in its acquire mode (owner 2026-09-30: same look, plus a fill box per leg and an
 * Acquire button), hosted here with what Collars knows — the pending order blocks on the combo(s)
 * under the clicked bucket, the group's live positions for the Before column, the live quotes for
 * the Market guidance. One Acquire books every FULLY PRICED block (each whole, §6.5) through
 * `AcquireAnalysisBlocks`, one call per session (an orphan books into its dead session); the undo
 * icon is Clear After State (= the Analysis panel's Revoke, block scope). Promise-based, like the
 * other Collars dialogs; the old saved-plan Apply After State dialog is untouched.
 */
@Component({
   selector: 'ets-collars-acquire-dialog',
   template: `
      <ets-pha-export-dialog #dlg mode="acquire" title="Apply After State"
         [visible]="visible" [groups]="groups" [busy]="busy"
         (acquireClicked)="onAcquire($event)" (revokeClicked)="onClear($event)" (closed)="onClose()"></ets-pha-export-dialog>
      <ets-generic-confirmation-dialog #confirm title="Clear After State"></ets-generic-confirmation-dialog>
   `,
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollarsAcquireDialogComponent implements OnDestroy {

   constructor(
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _toastr: ToastrService,
      private readonly _shellClient: ShellClientService,
      private readonly _messageBus: MessageBusService,
      private readonly _lastQuoteCache: LastQuoteCacheService,
      private readonly _analysisLocks: CollarsAnalysisLockService,
      private readonly _timestamps: TimestampsService,
      private readonly _displayNames: TradingInstrumentDisplayNameService,
      loggerService: LoggerService
   ) {
      this._logger = loggerService.createLogger('CollarsAcquireDialog');
      this._market = new OrderPreviewMarket(_lastQuoteCache, _messageBus, () => this.repaint());
   }

   private readonly _logger: Logger;
   private readonly _unsub = new Subject<void>();

   @ViewChild('dlg', { static: false }) dialog: PhaExportDialogComponent;
   @ViewChild('confirm', { static: false }) confirmDialog: GenericConfirmationDialogComponent;

   visible = false;
   busy = false;
   groups: PhaExportGroup[] = [];

   private _config: CollarsAcquireConfig;
   private _resolve: (result: CollarsAcquireResult) => void;
   private _applied = false;
   private _discarded = false;

   // Live market guidance: distinct instrument codes subscribed through the shared cache; torn down on close.
   private readonly _market: OrderPreviewMarket;

   /** Open on a scope; resolves when the window closes. Synchronous (the promise is returned at once). */
   show(config: CollarsAcquireConfig): Promise<CollarsAcquireResult> {
      this._config = config;
      this.groups = [];
      this._applied = false;
      this._discarded = false;
      this.busy = true;
      this.visible = true;
      this._changeDetector.detectChanges();
      const promise = new Promise<CollarsAcquireResult>(resolve => { this._resolve = resolve; });
      this.runLoad();
      return promise;
   }

   private async runLoad(): Promise<void> {
      try {
         await this.load();
      } catch (e) {
         this._logger.error('Collars acquire load failed', { error: e.stack || e });
         this._toastr.error('The Pending Orders of the Portfolio Hedging Analysis Could Not Be Read');
         this.groups = [];
      } finally {
         this.busy = false;
         this.repaint();
      }
   }

   /** Buckets + positions + the pending blocks of the combos under the scope → the window's groups. */
   private async load(): Promise<void> {
      const buckets = (await this._shellClient.processQuery<CollarsBucketDto[]>(new ListCollarsBuckets())) || [];
      const positions = (await this._shellClient.processQuery<CollarsPositionDto[]>(new ListCollarsPositions())) || [];
      const bucketById: { [id: string]: CollarsBucketDto } = {};
      buckets.forEach(b => { bucketById[b.bucketId] = b; });

      const comboIds = this.combosUnderScope(buckets, bucketById);
      const pending: AnalysisPendingBlockDto[] = [];
      if (comboIds.length > 0) {
         (await this._analysisLocks.comboStates(comboIds)).forEach(s => s.pendingBlocks.forEach(pb => pending.push(pb)));
      }
      // The scope is respected (owner 2026-09-30): a group shows its own blocks only; a combo its groups';
      // a portfolio every group's under every combo.
      const groupIds = this.groupsUnderScope(buckets, comboIds);
      this.groups = buildAcquireGroups(pending, positions, bucketById, {
         displayNameOf: t => this._displayNames.getDisplayNameForTicker(t) || t,
         // To the second, 12-hour with AM/PM, in the user's default time zone (as the Export window).
         exportedTextOf: iso => 'Exported ' + this._timestamps.getZonedFormattedDateTime(new Date(iso), 'dd-MMM-yy hh:mm:ss a'),
         blockIds: this._config.analysisBlockIds || [],
         groupIds
      });
      this._logger.debug(`Collars acquire load -> scope=${this._config.scopeBucketId} level=${this._config.level} combos=${comboIds.length} groups=${groupIds.length} pending=${pending.length} shown=${this.groups.reduce((n, g) => n + g.blocks.length, 0)}`);
      this.subscribeLiveQuotes();
   }

   /** The groups the window shows blocks for: the clicked group, or every group of the combos under the scope. */
   private groupsUnderScope(buckets: CollarsBucketDto[], comboIds: string[]): string[] {
      if (this._config.level === LEVEL_GROUP) { return [this._config.scopeBucketId]; }
      return buckets.filter(b => b.level === LEVEL_GROUP && comboIds.indexOf(b.parentBucketId) >= 0).map(b => b.bucketId);
   }

   private combosUnderScope(buckets: CollarsBucketDto[], bucketById: { [id: string]: CollarsBucketDto }): string[] {
      const scope = bucketById[this._config.scopeBucketId];
      if (!scope) { return []; }
      if (this._config.level === LEVEL_GROUP) { return scope.parentBucketId ? [scope.parentBucketId] : []; }
      if (this._config.level === LEVEL_COMBO) { return [scope.bucketId]; }
      return buckets.filter(b => b.level === LEVEL_COMBO && b.parentBucketId === scope.bucketId).map(b => b.bucketId);
   }

   // ---- Acquire ----------------------------------------------------------------------------------

   /** Book the fully priced blocks: one call per session, each leg's fill + the guidance beside it. */
   async onAcquire(blocks: PhaExportBlock[]): Promise<void> {
      if (this.busy || !blocks || blocks.length === 0) { return; }
      this.busy = true;
      this.repaint();
      const bySession: { [sessionId: string]: PhaExportBlock[] } = {};
      const order: string[] = [];
      blocks.forEach(b => {
         const sid = b.sessionId || '';
         if (!bySession[sid]) { bySession[sid] = []; order.push(sid); }
         bySession[sid].push(b);
      });
      let booked = 0;
      let failed = false;
      try {
         for (const sessionId of order) {
            const input = bySession[sessionId].map(b => ({
               blockId: b.blockId,
               fills: b.legs.map(l => ({ changeId: l.changeId, fillPx: l.fillPx, guidancePx: l.marketPx != null ? l.marketPx : null } as AnalysisFillInput))
            }));
            this._logger.info(`Collars acquire -> AcquireAnalysisBlocks session=${sessionId} blocks=${input.length}`, input);
            this._applied = true;
            try {
               await this._shellClient.processCommand(new AcquireAnalysisBlocks(sessionId, input));
               booked += input.length;
               // The in-app notice (CR-20260930-001): the analysis panel beside us re-reads at once,
               // never waiting for the server push an ESS dropout could lose.
               this._messageBus.publish({
                  topic: ANALYSIS_SESSION_TOUCHED_MESSAGE_TYPE,
                  payload: { sessionId: sessionId, reason: 'acquired' } as AnalysisSessionTouchedUIMessage
               });
            } catch (e) {
               // A refused command carries no text over WCF: say which step failed; the legs that booked are in the ledger.
               failed = true;
               this._logger.error('Collars acquire: AcquireAnalysisBlocks failed session=' + sessionId, { error: e.stack || e, booked });
               this._toastr.error(`The Orders Could Not Be Applied (${booked} of ${blocks.length} Booked) — Check the Ledger and the Log`);
               break;
            }
         }
         if (!failed) {
            this._toastr.success(`${booked} Order${booked === 1 ? '' : 's'} Applied`);
         }
         // Fresh rows: the blocks that booked are gone (the panel learns it from the push as well).
         await this.load();
         if (!failed && this.groups.length === 0) {
            this.close();
            return;
         }
      } catch (e) {
         this._logger.error('Collars acquire: re-read after acquire failed', { error: e.stack || e });
      } finally {
         this.busy = false;
         this.repaint();
      }
   }

   // ---- Clear After State (the undo icon) -------------------------------------------------------

   async onClear(b: PhaExportBlock): Promise<void> {
      if (this.busy || !b) { return; }
      try {
         await this.confirmDialog.show([
            `This will clear the ${b.isOrphaned ? 'orphaned ' : ''}pending order${b.name ? " '" + b.name + "'" : ''} of ${this.groupTitleOf(b)}.`,
            'Do you confirm?'
         ]);
      } catch (e) {
         return;
      }
      this.busy = true;
      this.repaint();
      try {
         await this._shellClient.processCommand(new RevokeAnalysisBlocks(b.sessionId, 'block', b.blockId, ''));
         this._logger.info('Collars acquire: block cleared block=' + b.blockId + ' session=' + b.sessionId);
         this._discarded = true;
         this._messageBus.publish({
            topic: ANALYSIS_SESSION_TOUCHED_MESSAGE_TYPE,
            payload: { sessionId: b.sessionId, reason: 'revoked' } as AnalysisSessionTouchedUIMessage
         });
         await this.load();
      } catch (e) {
         this._logger.error('Collars acquire: clear block failed block=' + b.blockId, { error: e.stack || e });
         this._toastr.error('The Pending Order Could Not Be Cleared (See the Log)');
      } finally {
         this.busy = false;
         this.repaint();
      }
   }

   private groupTitleOf(b: PhaExportBlock): string {
      const g = this.groups.find(x => x.blocks.indexOf(b) >= 0);
      return g ? g.title : 'this underlying';
   }

   // ---- Close ------------------------------------------------------------------------------------

   onClose(): void {
      if (this.busy) { return; }
      this.close();
   }

   private close(): void {
      this.visible = false;
      this.teardownLiveQuotes();
      this.repaint();
      if (this._resolve) {
         this._resolve({ applied: this._applied, discarded: this._discarded });
         this._resolve = null;
      }
   }

   private repaint(): void {
      this._changeDetector.detectChanges();
      if (this.dialog) { this.dialog.repaint(); }
   }

   // ---- Live market guidance (the Apply After State dialog's pattern) ---------------------------

   private subscribeLiveQuotes(): void {
      this._market.start(this.allLegs().map(l => {
         // Code is mandatory for tracking; legacy blocks may omit it.
         l.code = l.code || l.ticker;
         return l as PhaExportLeg & {code: string};
      }));
   }

   private teardownLiveQuotes(): void {
      this._market.stop();
   }

   private allLegs(): PhaExportLeg[] {
      const out: PhaExportLeg[] = [];
      this.groups.forEach(g => g.blocks.forEach(b => b.legs.forEach(l => out.push(l))));
      return out;
   }

   ngOnDestroy(): void {
      this._unsub.next();
      this._unsub.complete();
      this.teardownLiveQuotes();
   }
}
